/**
 * Salvation Team permissions.
 *
 * Module capability: Admin and SalvationAdmin get everything (every campus). SalvationLeader gets Read,
 * Converts.Manage, Assign, Team.Manage, Intake.Review, Milestones.Manage, Export and FollowUp.Log (the seeded role
 * rights in pg/identity/012) for the campuses they lead (the API answers 403 not_your_campus for any other), but not
 * Assignments.Correct or Settings.Manage. Any other user gets a capability only when they hold the matching
 * `Salvation.*` right.
 *
 * The rules live in `computeSalvationPermissions` (pure, no hooks) so the route gates (SalvationGate) and the sidebar
 * menu (salvationMenu.tsx via salvationMenuVisibility) evaluate exactly the same thing; SALVATION_PAGE_ACCESS says
 * which capability opens which page.
 */

import { useMemo } from 'react';
import { SALVATION_RIGHTS } from '@actschurch/ccms-sdk';
import type { MenuItemVisibility } from '../../layouts/menu/types';
import { useEnrichedAuth } from '../useEnrichedAuth';

interface SalvationAccessChecks {
  hasRole: (role: string) => boolean;
  hasPermission: (right: string) => boolean;
}

/** Every Salvation capability for a user, from plain role / right checks (no React). */
export function computeSalvationPermissions({ hasRole, hasPermission }: SalvationAccessChecks) {
  const isFullAccess = hasRole('Admin') || hasRole('SalvationAdmin');
  const isLeader = hasRole('SalvationLeader');

  /** Capability = full access, or the leader role when it grants it by default, or the explicit right. */
  const can = (right: string, leaderDefault: boolean) => isFullAccess || (isLeader && leaderDefault) || hasPermission(right);

  return {
    canRead: can(SALVATION_RIGHTS.Read, true),
    canManagePeople: can(SALVATION_RIGHTS.ConvertsManage, true),
    canAssign: can(SALVATION_RIGHTS.Assign, true),
    canCorrectAssignments: can(SALVATION_RIGHTS.AssignmentsCorrect, false),
    canManageTeam: can(SALVATION_RIGHTS.TeamManage, true),
    canReviewIntake: can(SALVATION_RIGHTS.IntakeReview, true),
    canManageMilestones: can(SALVATION_RIGHTS.MilestonesManage, true),
    canExport: can(SALVATION_RIGHTS.Export, true),
    canManageSettings: can(SALVATION_RIGHTS.SettingsManage, false),
    /** New campus teams, moving or switching off a team, making someone a campus leader. */
    isFullAccess,
    isLeader,
  };
}

export type SalvationCapabilities = ReturnType<typeof computeSalvationPermissions>;

export function useSalvationPermissions() {
  const { user, hasRole, hasPermission } = useEnrichedAuth();
  const perms = useMemo(() => computeSalvationPermissions({ hasRole, hasPermission }), [hasRole, hasPermission]);
  return { ...perms, userId: user?.id as string | undefined };
}

export type SalvationPermissions = ReturnType<typeof useSalvationPermissions>;

/** Who may open a Salvation page: one predicate per page, shared by the routes and the menu. */
export type SalvationPageAccess = (p: SalvationCapabilities) => boolean;

export const SALVATION_PAGE_ACCESS = {
  overview: (p) => p.canRead,
  people: (p) => p.canRead,
  person: (p) => p.canRead,
  /** New decisions: the review queue, Bring in decisions and saved sources (Salvation.Intake.Review). */
  intake: (p) => p.canReviewIntake,
  teams: (p) => p.canRead,
  settings: (p) => p.canManageSettings,
} satisfies Record<string, SalvationPageAccess>;

/** Menu visibility that matches the route gate (the menu context carries the same roles and rights). */
export function salvationMenuVisibility(...pages: SalvationPageAccess[]): MenuItemVisibility {
  return (ctx) => {
    const roles = ctx.userRoles ?? [];
    const rights = ctx.userPermissions ?? [];
    const perms = computeSalvationPermissions({
      hasRole: (role) => roles.includes(role),
      hasPermission: (right) => rights.includes(right),
    });
    return pages.some((allow) => allow(perms));
  };
}
